import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { AircallMengen as Mengen, AircallMengenZeile, ApiClient } from "@kundeninfo/api-client";
import { Auswahl, Hinweis, Tabelle } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../components/meHuellen.js";
import { berlinerTag } from "./tickets/ticketFormat.js";
import "./AircallMengen.css";

/**
 * DIE MENGEN JE KUNDE, LEITUNG UND RICHTUNG (Spec „Nacharbeitszeit, Leitung und Kunde je Anruf", Fassung 1.3, E299,
 * E302, E310, F21, F35, F41) im Dienst-Detail: Monatssummen, je Zeile je Tag aufklappbar — Anzahl der Anrufe neben der
 * Dauer, Gesprächszeit, Nacharbeit und ihre Summe, die Telefonzeit (`h:mm:ss`), davon auffällig. Zahlen rechtsbündig.
 * Darunter, was nicht abgerechnet wird: offene Anrufe, Nacharbeit ohne Anruf, Anrufe ohne Statushistorie. Leiste mit
 * festen Feldbreiten (kein Layoutwechsel durch die Auswahl); ein Monatswechsel setzt die Filter Kunde und Leitung
 * zurück.
 * Eine geänderte Schwelle (`schwelle`) lädt die Sicht neu (F41); Tageswerte einer überholten Ladung werden verworfen.
 * Seit Migration 052 (Spec „Aircall: Vermittlungen", E320, E322, V9): Telefonzeit (abgerechnet) aus dem Gespräch der
 * Agentin, Anrufdauer (informativ) daneben, Zahl der Anrufe ohne Statushistorie je Zeile.
 * `dauer` und `grenzen` spiegeln `sekundenAlsDauer` und `monatsGrenzen` der Domäne (`web` darf sie nicht importieren).
 * Block `acm`.
 */

export type AircallMengenClient = Pick<ApiClient, "aircallMengen">;

/** Sekunden als `h:mm:ss` (Stunden ohne Obergrenze). */
export function dauer(sekunden: number): string {
  const s = Math.max(0, Math.trunc(sekunden));
  return `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:`
    + `${String(s % 60).padStart(2, "0")}`;
}

/** Die letzten zwölf Monate nach Berliner Kalender (F13), jüngster zuerst; Vorgabe ist der Vormonat. */
export function monate(jetzt: Date): string[] {
  const [jahr, monat] = berlinerTag(jetzt.toISOString()).split("-").map(Number) as [number, number];
  const aus: string[] = [];
  for (let i = 1; i <= 12; i++) {
    const d = new Date(Date.UTC(jahr, monat - i, 1));
    aus.push(d.toISOString().slice(0, 7));
  }
  return aus;
}

function grenzen(monat: string): { von: string; bis: string } {
  const [j, m] = monat.split("-").map(Number) as [number, number];
  const letzter = new Date(Date.UTC(j, m, 0)).getUTCDate();
  return { von: `${monat}-01`, bis: `${monat}-${String(letzter).padStart(2, "0")}` };
}

const RICHTUNG = { INBOUND: "eingehend", OUTBOUND: "ausgehend" } as const;

function schluessel(z: AircallMengenZeile): string {
  return `${z.customerId}|${z.leitungId}|${z.direction}`;
}

export function AircallMengen(p: {
  client: AircallMengenClient; jetzt?: Date;
  /** Die geltende Schwelle in Sekunden — ändert sie sich, lädt die Sicht neu (F41). */
  schwelle?: number | null;
}): ReactElement {
  const auswahl = useMemo(() => monate(p.jetzt ?? new Date()), [p.jetzt]);
  const [monat, setMonat] = useState(auswahl[1] ?? auswahl[0]!);
  const [kunde, setKunde] = useState("");
  const [leitung, setLeitung] = useState("");
  const [mengen, setMengen] = useState<Mengen | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [tage, setTage] = useState<Record<string, AircallMengenZeile[]>>({});
  const laden = p.client.aircallMengen;
  /** Zählt die Ladungen; eine Tagesantwort aus einer überholten Ladung (Monat oder Schwelle gewechselt) verfällt. */
  const ladung = useRef(0);

  useEffect(() => {
    let abgebrochen = false;
    ladung.current += 1;
    setMengen(null);
    setTage({});
    setFehler(null);
    laden(grenzen(monat))
      .then((m) => { if (!abgebrochen) setMengen(m); })
      .catch(() => { if (!abgebrochen) setFehler("Die Mengen konnten nicht geladen werden."); });
    return () => { abgebrochen = true; };
  }, [laden, monat, p.schwelle]);

  function monatWaehlen(neu: string): void {
    setMonat(neu);
    setKunde("");
    setLeitung("");
  }

  const zeilen = (mengen?.zeilen ?? []).filter((z) => (kunde === "" || z.customerId === kunde)
    && (leitung === "" || z.leitungId === leitung));
  const kunden = [...new Map((mengen?.zeilen ?? []).map((z) => [z.customerId, z.kundeName ?? "Kunde ohne Namen"]))];
  const leitungen = [...new Map((mengen?.zeilen ?? []).map((z) => [z.leitungId, z.leitungName ?? z.aircallNumberId]))];
  const ohneAbgleich = zeilen.reduce((s, z) => s + z.ohneAbgleich, 0);

  async function jeTag(z: AircallMengenZeile): Promise<void> {
    const k = schluessel(z);
    if (tage[k] !== undefined) { setTage(({ [k]: _weg, ...rest }) => rest); return; }
    const fuer = ladung.current;
    try {
      const m = await laden({ ...grenzen(monat), kunde: z.customerId, leitung: z.leitungId, jeTag: true });
      if (ladung.current !== fuer) return;
      setFehler(null);
      setTage((t) => ({ ...t, [k]: m.zeilen.filter((x) => x.direction === z.direction) }));
    } catch {
      if (ladung.current === fuer) setFehler("Die Tageswerte konnten nicht geladen werden.");
    }
  }

  const zelle = (z: AircallMengenZeile, tag: boolean) => (
    <>
      <td>{tag ? z.tag : z.kundeName ?? "Kunde ohne Namen"}</td>
      <td>{tag ? "" : z.leitungName ?? z.aircallNumberId}</td>
      <td>{RICHTUNG[z.direction]}</td>
      <td className="ki-num" data-ausrichtung="rechts">{z.anrufe}</td>
      <td className="ki-num" data-ausrichtung="rechts">{z.angenommen}</td>
      <td className="ki-num" data-ausrichtung="rechts">{dauer(z.gespraechSekunden)}</td>
      <td className="ki-num" data-ausrichtung="rechts">{dauer(z.nacharbeitSekunden)}</td>
      <td className="ki-num" data-ausrichtung="rechts">{dauer(z.telefonzeitSekunden)}</td>
      <td className="ki-num" data-ausrichtung="rechts">{dauer(z.anrufdauerSekunden)}</td>
      <td className="ki-num" data-ausrichtung="rechts">{z.auffaellig} · {dauer(z.auffaelligSekunden)}</td>
      <td className="ki-num" data-ausrichtung="rechts">{z.ohneAbgleich}</td>
    </>
  );

  return (
    <section className="acm" aria-label="Mengen">
      <h3 className="acm__titel">Mengen</h3>
      <div className="acm__leiste">
        <Auswahl beschriftung="Monat" breite="9em" wert={monat} onGeaendert={(d) => monatWaehlen(d.wert)}>
          {auswahl.map((m) => <option key={m} value={m}>{`${m.slice(5, 7)}/${m.slice(0, 4)}`}</option>)}
        </Auswahl>
        <Auswahl beschriftung="Kunde" breite="16em" wert={kunde} onGeaendert={(d) => setKunde(d.wert)}>
          <option value="">Alle Kunden</option>
          {kunden.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </Auswahl>
        <Auswahl beschriftung="Leitung" breite="14em" wert={leitung} onGeaendert={(d) => setLeitung(d.wert)}>
          <option value="">Alle Leitungen</option>
          {leitungen.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </Auswahl>
      </div>
      {fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}
      {mengen === null && fehler === null && <p className="acm__leise" role="status">Lädt …</p>}
      {mengen !== null && zeilen.length === 0 && <p className="acm__leise">In diesem Monat nichts abzurechnen.</p>}
      {zeilen.length > 0 && (
        <Tabelle stapeln>
          <table className="acm__tabelle">
            <thead>
              <tr>
                <th scope="col">Kunde</th><th scope="col">Leitung</th><th scope="col">Richtung</th>
                <th scope="col" data-ausrichtung="rechts">Anrufe</th>
                <th scope="col" data-ausrichtung="rechts">angenommen</th>
                <th scope="col" data-ausrichtung="rechts">Gesprächszeit</th>
                <th scope="col" data-ausrichtung="rechts">Nacharbeit</th>
                <th scope="col" data-ausrichtung="rechts">Telefonzeit (abgerechnet)</th>
                <th scope="col" data-ausrichtung="rechts">Anrufdauer (informativ)</th>
                <th scope="col" data-ausrichtung="rechts">auffällig</th>
                <th scope="col" data-ausrichtung="rechts">ohne Statushistorie</th>
                <th scope="col"><span className="acm__unsichtbar">Je Tag</span></th>
              </tr>
            </thead>
            <tbody>
              {zeilen.flatMap((z) => [
                <tr key={schluessel(z)}>
                  {zelle(z, false)}
                  <td>
                    <KiKnopf name={`${z.kundeName ?? ""} ${z.leitungName ?? ""} ${RICHTUNG[z.direction]} je Tag`}
                      ausgeklappt={tage[schluessel(z)] !== undefined} onClick={() => { void jeTag(z); }}>
                      Je Tag
                    </KiKnopf>
                  </td>
                </tr>,
                ...(tage[schluessel(z)] ?? []).map((t) => (
                  <tr key={`${schluessel(z)}|${t.tag}`} className="acm__tag">{zelle(t, true)}<td /></tr>
                )),
              ])}
            </tbody>
          </table>
        </Tabelle>
      )}
      {mengen !== null && (
        <dl className="acm__summen">
          <div><dt>Offen (ohne Zuordnung)</dt><dd className="ki-num">{mengen.offeneAnrufe}</dd></div>
          <div><dt>Nacharbeit ohne Anruf</dt>
            <dd className="ki-num">{mengen.nacharbeitOhneAnruf.spannen} · {dauer(mengen.nacharbeitOhneAnruf.sekunden)}</dd>
          </div>
          <div><dt>Anrufe ohne Statushistorie</dt><dd className="ki-num">{ohneAbgleich}</dd></div>
          <div><dt>Auffällig ab</dt><dd className="ki-num">{mengen.schwelle / 60} min</dd></div>
        </dl>
      )}
    </section>
  );
}
